import React, { useEffect, useMemo, useState } from "react"
import Timer from "../Auction/Timer"
import BankArea from "./BankArea"
import DiceArea from "./DiceArea"
import GameButtons from "./GameButtons"
import PlayersList from "./PlayersList"
import CreateTradeButton from "./CreateTradeButton"
import { MainButton } from "@/Components/MainButton"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData, setState } from "@/store/globalSlice"
import { isMobile } from "@/utils/utils"
import {
  getAllUnsoldProperties,
  getCellByOrderAndMapId,
  getCellGroup,
} from "@/utils/gameHelpers"
import PropertySmallCard from "./PropertySmallCard"
import FastPaceControl from "./FastPaceControl"
import FastPaceVotePanel from "@/Components/FastPaceVotePanel"

// Sky blue against the pace control's violet: the same key, plainly a
// different one.
const HELP_COLOR = "#38bdf8"

interface SidebarProps {}

const SidebarLeft: React.FC<SidebarProps> = () => {
  const dispatch = useAppDispatch()
  const mapId = useAppSelector(
    (state) => state.global.myGame?.settings.mapId as string
  )
  const players = useAppSelector(
    (state) => state.global.myGame?.situation?.players
  )
  const historyItems = useAppSelector(
    (state) => state.global.myGame?.situation?.historyItems
  )
  const allBoughtProperties = useMemo(
    () => players?.flatMap((player) => player.properties),
    [players]
  )
  const allUnsoldProperties = useMemo(
    () => getAllUnsoldProperties(mapId as string, allBoughtProperties || []),
    [mapId, allBoughtProperties?.length]
  )

  // The most recent completed auction (won or tied) drives the "last auction
  // results" button. Auction results are persisted in the game history, so we
  // read the latest matching entry rather than transient situation state.
  const lastAuctionItem = useMemo(
    () =>
      [...(historyItems || [])]
        .reverse()
        .find(
          (item): item is IHistoryAuctionWon | IHistoryAuctionTied =>
            item.type === "auction-won" || item.type === "auction-tied"
        ),
    [historyItems]
  )

  // The guide is a labelled diagram of the landscape screen: a wide board with
  // a column of labels down either side of it. That shape does not survive a
  // portrait phone, where the diagram would have to shrink past the point of
  // being readable to leave room for the labels. Hidden rather than degraded.
  // Re-read on every orientation change, since `isPortrait` is dispatched from
  // App.tsx.
  const isPortrait = useAppSelector((state) => state.global.isPortrait)
  const canShowGuide = !isPortrait && !isMobile()

  // The diagram, not the written panel: the answer to "what is this thing on my
  // screen" is easiest to give by pointing at a picture of the screen. The
  // panel is still one click away, from the footer of the diagram.
  const handleOpenHelp = () => {
    dispatch(setState({ helpTourActive: true }))
  }

  const handleShowLastAuction = () => {
    if (!lastAuctionItem) return
    dispatch(
      setModalData({
        auctionResult: {
          visible: true,
          propertyOrder: lastAuctionItem.propertyOrder,
        },
      })
    )
  }

  const lastAuctionCell = lastAuctionItem
    ? getCellByOrderAndMapId(lastAuctionItem.propertyOrder, mapId as string)
    : undefined
  const lastAuctionGroup = lastAuctionCell
    ? getCellGroup(mapId as string, lastAuctionCell)
    : undefined

  return (
    // Mirror of Sidebar.tsx: as tall as the playable box (screen minus notch /
    // camera / home-indicator insets), and as wide as half of what the board's
    // 100vh square leaves inside it — the columns are what pay for the board
    // running full height, so neither one ever sits over it.
    <div className="flex flex-col h-[var(--play-h)] justify-between w-[calc((var(--play-w)-100vh)/2)] z-[20]  relative pr-4">
      {/* The game's pace, at the head of the player column: the button that
          puts fast pace to the table, the badge once the table has agreed, or
          the vote panel while it is deciding. One slot, one thing in it at a
          time — see FastPaceControl, which stands down for the panel.

          Positioned against this column's own edges rather than the viewport's,
          and cut to the same width as a player card below it (`left-0 right-4`
          is the column's content box, `w-[98%]` is the card's share of it), so
          whatever is in it reads as the top of this column.

          It draws on the empty strip PlayersList keeps above its topmost card,
          which on a desktop widens from 10rem to 16.5rem while a vote is open —
          on both sides at once, so the two columns keep the identical box their
          row-matching depends on. That is the panel's whole height budget:
          1.25rem of inset here, ~10rem of panel, and the 4.32rem the topmost
          card's "your turn" badge floats above it. Grow the panel past that and
          it lands on the badge, so the strip has to grow with it. A phone is
          the deliberate exception: the strip stays put there and the panel
          overlaps the card — see PlayersList. */}
      <div className="absolute top-5 left-0 right-4 z-30 flex justify-end">
        <div className="w-[98%] flex flex-row items-start gap-3">
          {/* Help sits in the outermost corner of this column, the mirror of
              the settings/pause cluster in the opposite one, and shares this row
              with the pace control rather than stacking above it — stacking
              would push the vote panel down onto the topmost player card's
              "your turn" badge. Same small MainButton skin as "Make game fast
              pace", in its own colour, and the two are pushed to opposite ends
              of the row so neither reads as part of the other. The word "Help"
              is on the key itself, not in a tooltip: a player who does not know
              what the screen is will not hover a question mark to find out. */}
          {canShowGuide && (
            <MainButton
              baseColor={HELP_COLOR}
              size="sm"
              onClick={handleOpenHelp}
              className="!h-8 !min-w-0 shrink-0 !px-3"
              data-testid="game.help-button"
              aria-label="How the screen works"
              title="How the screen works — a labelled diagram of the game"
            >
              <div className="flex flex-row items-center gap-2">
                <i className="fa-solid fa-circle-question text-white text-xs" />
                <span className="text-xs text-white/90 font-poppins font-semibold tracking-wide uppercase">
                  Help
                </span>
              </div>
            </MainButton>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-row justify-end">
              <FastPaceControl />
            </div>
            <FastPaceVotePanel />
          </div>
        </div>
      </div>
      <PlayersList
        sidebar="left"
        situationPlayers={players || []}
      ></PlayersList>
      {/* Fixed-height bottom slot, mirrored in Sidebar.tsx, so the players list
          above gets the same box on both sides and the two lists stay aligned. */}
      <div className="flex flex-col justify-end gap-3 h-[calc(var(--play-h)*0.3)] shrink-0">
        {/* Same MainButton skin as Create Trade below. Sea green base (mirroring
            Create Trade's light blue) reads clearly as its own action against
            the sidebar, and the taller "base" size gives room to show the
            auctioned property's small card alongside the label. */}
        {lastAuctionItem && (
          <MainButton
            baseColor="#2e8b70"
            size="base"
            onClick={handleShowLastAuction}
            className="group ml-4 w-[calc(100%-1rem)] !h-16 !px-4"
            data-testid="game.last-auction-result-button"
            title="View the results of the most recent auction"
            aria-label="Last auction results"
          >
            <div className="flex w-full flex-row items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#12261f] ring-1 ring-black/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]">
                <i className="fa-solid fa-gavel text-emerald-300 text-sm transition-transform duration-150 group-hover:scale-110"></i>
              </span>
              {lastAuctionCell && lastAuctionGroup && (
                <div className="shrink-0 pointer-events-none">
                  <PropertySmallCard
                    property={lastAuctionCell}
                    group={lastAuctionGroup}
                    noAnimations
                    // On a phone this button shares a narrow sidebar with the
                    // label and the chevron, so the card comes down a notch to give
                    // "Last Auction Results" a little more room. Desktop keeps the
                    // standard card.
                    sizeClassName={isMobile() ? "h-9 w-[1.6rem]" : undefined}
                  />
                </div>
              )}
              <span className="min-w-0 flex-1 text-left font-poppins text-sm font-bold uppercase leading-none tracking-wide text-emerald-50/90 group-hover:text-white">
                Last Auction Results
              </span>
              <i className="fa-solid fa-chevron-right text-emerald-50/50 text-[0.6rem] transition-transform duration-150 group-hover:translate-x-0.5"></i>
            </div>
          </MainButton>
        )}
        <CreateTradeButton />
        <BankArea allUnsoldProperties={allUnsoldProperties}></BankArea>
        {/* <DiceArea></DiceArea> */}
      </div>
    </div>
  )
}

export default React.memo(SidebarLeft)
